import React, { useState } from 'react';
import css from './LevelsSection.module.css';
import Field, { hasDataInFields } from '../../containers/PageBuilder/Field';

// Pulls the raw markdown/text string out of a Field-shaped data object or string
const getRawContent = field => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field.content || field.markdown || field.text || '';
};

// Extracts string content from title field safely
const getTitleContent = titleField => {
    if (!titleField) return '';
    if (typeof titleField === 'string') return titleField;
    return titleField.content || titleField.text || '';
};

// Splits a block's text into { whatItMeans, approxActfl } using labeled lines, e.g.:
// **What it means:** Basic familiar phrases...
// **Approx ACTFL:** Novice Mid – Novice High
// Supports variations: Approx ACTFL, Approx. ACTFL, optional asterisks, colons inside/outside, Windows/Unix line breaks
const parseLevelText = field => {
    const raw = getRawContent(field).replace(/\r\n/g, '\n').trim();
    if (!raw) return { whatItMeans: '', approxActfl: '' };

    // Match "What it means" pattern
    const whatMatch = raw.match(
        /(?:\*{1,2})?What it means:?(?:\*{1,2})?:?\s*([\s\S]*?)(?=(?:\*{1,2})?Approx\.?\s*ACTFL|$)/i
    );

    // Match "Approx. ACTFL" or "Approx ACTFL" pattern
    const actflMatch = raw.match(
        /(?:\*{1,2})?Approx\.?\s*ACTFL:?(?:\*{1,2})?:?\s*([\s\S]*?)$/i
    );

    let whatItMeans = whatMatch ? whatMatch[1].trim() : '';
    let approxActfl = actflMatch ? actflMatch[1].trim() : '';

    // Strip any accidental leading or trailing markdown bold syntax
    whatItMeans = whatItMeans.replace(/^\*\*|\*\*$/g, '').trim();
    approxActfl = approxActfl.replace(/^\*\*|\*\*$/g, '').trim();

    // Fallback if no specific labels were found: split by paragraphs
    if (!whatItMeans && !approxActfl) {
        const parts = raw.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
        if (parts.length >= 2) {
            whatItMeans = parts[0];
            approxActfl = parts[1];
        } else if (parts.length === 1) {
            whatItMeans = parts[0];
        }
    }

    return { whatItMeans, approxActfl };
};

const LevelsSection = ({ blocks = [], options, title, description, sectionId }) => {
    const fieldComponents = options?.fieldComponents;
    const fieldOptions = { fieldComponents };

    const [expanded, setExpanded] = useState(0); // first row open by default on mobile (A1)

    const hasTitle = hasDataInFields([title], fieldOptions);
    const hasDescription = hasDataInFields([description], fieldOptions);

    // Every block whose blockId starts with "level-" becomes a table row, in Console order
    let levelBlocks = blocks.filter(block => block?.blockId?.startsWith('level-'));
    // Fallback: If no blocks start with "level-", take all blocks except footer
    if (levelBlocks.length === 0) {
        levelBlocks = blocks.filter(
            block => block?.blockId !== 'levels-footer' && !block?.blockId?.includes('footer')
        );
    }

    // A separate block holds the footer paragraph + CTA button
    const footerBlock =
        blocks.find(block => block?.blockId === 'levels-footer' || block?.blockId?.includes('footer')) ||
        blocks.find(block => block?.callToAction);

    const hasFooterText = hasDataInFields([footerBlock?.text], fieldOptions);
    const rawCta = footerBlock?.callToAction;
    const hasFooterCta = hasDataInFields([rawCta], fieldOptions);

    // Ensure CTA label has arrow "→" if not already present
    const ctaData = hasFooterCta
        ? {
              ...rawCta,
              content:
                  rawCta.content &&
                  !rawCta.content.includes('→') &&
                  !rawCta.content.includes('->')
                      ? `${rawCta.content} →`
                      : rawCta.content,
          }
        : null;

    const toggle = index => {
        setExpanded(prev => (prev === index ? -1 : index));
    };

    return (
        <section id={sectionId || 'levels-section'} className={css.levelsSection}>
            <div className={css.header}>
                {hasTitle ? (
                    <div className={css.titleWrapper}>
                        <Field data={title} className={css.title} options={fieldOptions} />
                    </div>
                ) : null}

                {hasDescription ? (
                    <div className={css.descriptionWrapper}>
                        <Field data={description} className={css.description} options={fieldOptions} />
                    </div>
                ) : null}
            </div>

            {/* ---------- Desktop table ---------- */}
            <div className={css.tableWrapper}>
                <div className={css.tableHeader}>
                    <span className={css.colLevel}>Level</span>
                    <span className={css.colWhat}>What it means</span>
                    <span className={css.colActfl}>Approx. ACTFL</span>
                </div>

                {levelBlocks.map((block, index) => {
                    const hasBlockTitle = hasDataInFields([block?.title], fieldOptions);
                    const { whatItMeans, approxActfl } = parseLevelText(block?.text);
                    const titleText = getTitleContent(block?.title);
                    const isAltRow = index % 2 === 1;

                    if (!hasBlockTitle && !titleText) return null;

                    return (
                        <div
                            key={block.blockId || `level-${index}`}
                            className={`${css.tableRow} ${isAltRow ? css.tableRowAlt : ''}`}
                        >
                            <div className={css.colLevel}>
                                <span className={css.levelName}>
                                    {titleText || <Field data={block.title} options={fieldOptions} />}
                                </span>
                            </div>
                            <div className={css.colWhat}>
                                <span className={css.cellText}>{whatItMeans}</span>
                            </div>
                            <div className={css.colActfl}>
                                <span className={css.cellText}>{approxActfl}</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ---------- Mobile accordion ---------- */}
            <div className={css.accordionWrapper}>
                <div className={css.accordionContainer}>
                    {levelBlocks.map((block, index) => {
                        const hasBlockTitle = hasDataInFields([block?.title], fieldOptions);
                        const { whatItMeans, approxActfl } = parseLevelText(block?.text);
                        const titleText = getTitleContent(block?.title);
                        const isOpen = expanded === index;

                        if (!hasBlockTitle && !titleText) return null;

                        return (
                            <div
                                key={block.blockId || `acc-${index}`}
                                className={`${css.accordionItem} ${isOpen ? css.accordionItemOpen : ''}`}
                            >
                                <button
                                    type="button"
                                    className={css.accordionHeader}
                                    onClick={() => toggle(index)}
                                    aria-expanded={isOpen}
                                >
                                    <span className={css.accordionLabel}>
                                        {isOpen ? <strong className={css.levelPrefix}>Level:&nbsp;</strong> : null}
                                        <span className={css.accordionTitleText}>
                                            {titleText || <Field data={block.title} options={fieldOptions} />}
                                        </span>
                                    </span>
                                    <svg
                                        className={`${css.chevron} ${isOpen ? css.chevronOpen : ''}`}
                                        width="18"
                                        height="18"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="6 8 10 12 14 8" />
                                    </svg>
                                </button>

                                {isOpen ? (
                                    <div className={css.accordionBody}>
                                        <div className={css.accordionFieldGroup}>
                                            <p className={css.accordionFieldLabel}>What it means</p>
                                            <p className={css.accordionFieldValue}>{whatItMeans}</p>
                                        </div>

                                        <div className={css.accordionFieldGroup}>
                                            <p className={css.accordionFieldLabel}>Approx. ACTFL</p>
                                            <p className={css.accordionFieldValue}>{approxActfl}</p>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ---------- Footer text ---------- */}
            {hasFooterText ? (
                <div className={css.footerTextWrapper}>
                    <Field data={footerBlock.text} className={css.footerText} options={fieldOptions} />
                </div>
            ) : null}

            {/* ---------- Footer CTA button (centered) ---------- */}
            {ctaData ? (
                <div className={css.ctaWrapper}>
                    <Field data={ctaData} className={css.ctaButton} options={fieldOptions} />
                </div>
            ) : null}
        </section>
    );
};

export default LevelsSection;